import React, { useState, useEffect, useMemo, useCallback } from 'react'
import ReactFlow, { 
  Background, 
  Controls, 
  Edge, 
  Node, 
  ReactFlowInstance, 
  MiniMap, 
  addEdge, 
  Panel,
  useReactFlow,
} from 'reactflow'
import dagre from 'dagre'
import 'reactflow/dist/style.css'
import { MessageNode } from './message-node'
import { DataNode } from './data-node'
import { PhotoNode } from './photo-node'
import { DescriberNode } from './describer-node'
import { SynthesizerNode } from './synthesizer-node'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Send, Loader2 } from 'lucide-react'
import graphchatLogo from '@/assets/graphchat-logo.svg'
import { CommandMenu } from '@/components/ui/command-menu'
import { analyzeImage } from '@/services/image-services'
import { getWebSocketUrl, API_URL } from '@/config'
import { PhotoMessage, DescriberMessage } from '../../../shared/ws-messages';
import type { Message } from '@/types/message'
import { useAuth } from '@clerk/clerk-react'
import { withClerkAuthHeader } from '@/lib/clerk-transport'

// Layout settings
const NODE_WIDTH = 300
const NODE_HEIGHT = 160
const LAYOUT_OPTIONS = {
  rankdir: 'TB',    // top-to-bottom layout
  align: 'UL',      // upper-left alignment
  nodesep: 150,     // horizontal spacing between nodes
  ranksep: 180,     // vertical spacing between ranks
  edgesep: 100,      // minimum edge spacing
}

// Helper function to create a dagre graph and get node positions
function getLayoutedElements(nodes: Node[], edges: Edge[]) {
  const g = new dagre.graphlib.Graph()
  g.setGraph(LAYOUT_OPTIONS)
  g.setDefaultEdgeLabel(() => ({}))

  // Separate message nodes and independent nodes (photo, data, synthesizer, etc.)
  const independentNodeTypes = ['photo', 'data', 'synthesizer', 'describer']
  const messageNodes = nodes.filter(node => !independentNodeTypes.includes(node.type))
  const independentNodes = nodes.filter(node => independentNodeTypes.includes(node.type))

  // Add only message nodes to the graph for automatic layout
  messageNodes.forEach((node) => {
    g.setNode(node.id, { 
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      label: node.id
    })
  })

  // Add edges to the graph (only between message nodes)
  edges.forEach((edge) => {
    const sourceNode = nodes.find(n => n.id === edge.source)
    const targetNode = nodes.find(n => n.id === edge.target)
    if (!independentNodeTypes.includes(sourceNode?.type || '') && 
        !independentNodeTypes.includes(targetNode?.type || '')) {
      g.setEdge(edge.source, edge.target)
    }
  })

  // Apply the layout
  dagre.layout(g)

  // Get the positioned message nodes
  const layoutedMessageNodes = messageNodes.map((node) => {
    const nodeWithPosition = g.node(node.id)
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
      style: {
        width: NODE_WIDTH,
      },
    }
  })

  // Keep independent nodes at their current positions
  const persistentIndependentNodes = independentNodes.map(node => ({
    ...node,
    // Ensure position is valid
    position: {
      x: Number.isFinite(node.position.x) ? node.position.x : Math.max(0, Math.random() * 500 + 600),
      y: Number.isFinite(node.position.y) ? node.position.y : Math.max(0, Math.random() * 400 + 100)
    },
    style: {
      width: NODE_WIDTH,
    },
  }))

  // Return both layouted message nodes and independent nodes with preserved positions
  return { 
    nodes: [...layoutedMessageNodes, ...persistentIndependentNodes], 
    edges 
  }
}

// Helper function to check if a node has user message descendants
function hasUserDescendants(nodeId: string, nodes: Node[], edges: Edge[]): boolean {
  const childEdges = edges.filter(edge => edge.source === nodeId)
  if (childEdges.length === 0) return false

  for (const edge of childEdges) {
    const childNode = nodes.find(n => n.id === edge.target)
    if (!childNode) continue
    
    if (childNode.data.message?.role === 'user') {
      return true
    }
    
    // Recursively check children
    if (hasUserDescendants(childNode.id, nodes, edges)) {
      return true
    }
  }
  
  return false
}

interface FlowChartProps {
  rootNode: Message | null
  allNodes?: Record<string, Message>
  onSelectResponse: (nodeId: string) => void
  onRepath?: (nodeId: string) => void
  onSendMessage: (
    message:
      | string
      | {
          type: string
          path?: string[]
          data?: Record<string, any>
          [key: string]: any
        }
  ) => void
  isLoading?: boolean
  readyState: number
  children?: React.ReactNode
  onOptimizeLayout?: (optimizeLayout: () => void) => void
  sessionId: string
}

// Node types
const nodeTypes = {
  message: MessageNode,
  photo: PhotoNode,
  data: DataNode,
  describer: DescriberNode,
  synthesizer: SynthesizerNode
}

export function FlowChart({ 
  rootNode, 
  allNodes = {}, 
  onSelectResponse, 
  onRepath,
  onSendMessage,
  isLoading = false,
  readyState,
  children,
  onOptimizeLayout,
  sessionId
}: FlowChartProps) {
  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null)
  const [commandOpen, setCommandOpen] = useState(false)
  const [currentNode, setCurrentNode] = useState<Node | null>(null)
  const { getToken } = useAuth()
  const { project } = useReactFlow()

  // Memoize the conversion of message tree to flow
  const convertedFlow = useMemo(() => {
    if (!rootNode) {
      console.log('No root node provided for flow conversion');
      return null;
    }
    console.log('Starting flow conversion with root node:', {
      id: rootNode.id,
      role: rootNode.role,
      metadata: rootNode.metadata,
      childCount: rootNode.children?.length,
      allNodesCount: Object.keys(allNodes).length
    });

    // Add allNodes to the root message for processing
    const messageWithAllNodes = {
      ...rootNode,
      nodes: allNodes
    };
    
    return convertMessageTreeToFlow(messageWithAllNodes);
  }, [rootNode, allNodes]);

  // Function to optimize layout
  const optimizeLayout = useCallback(() => {
    if (nodes.length === 0) return

    // Re-layout all nodes
    const { nodes: layoutedNodes } = getLayoutedElements(nodes, edges)
    
    setNodes(layoutedNodes)

    // Fit view to show all nodes
    requestAnimationFrame(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({ 
          padding: 0.2, 
          duration: 200,
          includeHiddenNodes: false
        })
      }
    })
  }, [nodes, edges, reactFlowInstance])

  // Expose optimize layout function through props
  useEffect(() => {
    if (onOptimizeLayout) {
      onOptimizeLayout(optimizeLayout)
    }
  }, [optimizeLayout, onOptimizeLayout])

  // Function to create a data node from a photo
  const handleCreateDataNode = useCallback(async (photoNodeId: string, imageB64: string) => {
    try {
      // Convert base64 to blob
      const response = await fetch(`data:image/jpeg;base64,${imageB64}`);
      const blob = await response.blob();
      
      // Upload to worker first
      const token = await getToken()
      const headers = withClerkAuthHeader(
        {
          'X-File-Name': encodeURIComponent('generated_image.jpg'),
          'Content-Type': 'image/jpeg'
        },
        token
      )
      const uploadResponse = await fetch(`${API_URL}/ws/${sessionId}/upload`, {
        method: 'POST',
        headers,
        body: blob
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${await uploadResponse.text()}`);
      }

      const { id, url } = await uploadResponse.json();

      // Send data node message with URL and isGenerated flag
      const message = {
        type: 'data' as const,
        data: {
          filename: 'generated_image.jpg',
          content: url,
          mimeType: 'image/jpeg',
          isUrl: true,
          isGenerated: true  // Add flag to indicate this is a generated image
        },
        path: [photoNodeId]  // Set the photo node as parent
      };
      onSendMessage(message);
    } catch (error) {
      console.error('Error creating data node:', error);
    }
  }, [onSendMessage, sessionId]);

  // Process root node changes
  useEffect(() => {
    if (!rootNode || !convertedFlow) {
      console.log('No root node or converted flow available')
      return
    }

    const { nodes: newNodes, edges: newEdges } = convertedFlow;
    
    console.log('Flow conversion complete:', { 
      rootId: rootNode.id,
      nodeCount: newNodes.length,
      edgeCount: newEdges.length,
      nodeTypes: newNodes.map(n => ({
        id: n.id,
        type: n.type,
        metadata: n.data.metadata
      }))
    });

    // Get maps of existing nodes for efficient lookup
    const existingNodesMap = new Map(nodes.map(node => [node.id, node]))
    const existingPhotoPositions = new Map(
      nodes.filter(node => node.type === 'photo')
        .map(node => [node.id, node.position])
    )
    
    // Check if we actually need to update
    const hasContentChanges = newNodes.some(newNode => {
      const existingNode = existingNodesMap.get(newNode.id)
      return !existingNode || 
        JSON.stringify(existingNode.data.message) !== JSON.stringify(newNode.data.message) ||
        existingNode.type !== newNode.type
    })

    if (!hasContentChanges && nodes.length === newNodes.length) {
      console.log('No changes in nodes, skipping update')
      return
    }

    try {
      // Add handlers and flags to nodes
      const nodesWithHandlers = newNodes.map(node => {
        // For photo nodes, preserve their existing position
        if (node.type === 'photo') {
          const existingPosition = existingPhotoPositions.get(node.id)
          return {
            ...node,
            draggable: true,
            position: existingPosition || node.position,
            data: {
              ...node.data,
              onCreateDataNode: handleCreateDataNode
            }
          }
        }

        // For message nodes, add normal handlers
        return {
          ...node,
          draggable: true,
          data: {
            ...node.data,
            hasUserDescendants: hasUserDescendants(node.id, newNodes, newEdges),
            onClick: node.data.message?.role === 'assistant' ? () => onSelectResponse(node.id) : undefined,
            onRepath: node.data.message?.role === 'assistant' ? () => onRepath?.(node.id) : undefined
          }
        }
      })

      // Only run layout if we have content changes
      const layoutedNodes = hasContentChanges ? 
        getLayoutedElements(nodesWithHandlers, newEdges).nodes :
        nodesWithHandlers;
      
      // Update state with new layout
      setNodes(layoutedNodes)
      setEdges(newEdges)

      // Only zoom to new nodes if we have content changes
      if (hasContentChanges && reactFlowInstance) {
        // Get newly added nodes (excluding photos)
        const addedNodes = newNodes.filter(node => 
          !existingNodesMap.has(node.id) && node.type !== 'photo'
        )

        if (addedNodes.length > 0) {
          requestAnimationFrame(() => {
            // Find the layouted positions of the new nodes
            const newLayoutedNodes = layoutedNodes.filter(node => 
              addedNodes.some(newNode => newNode.id === node.id)
            )
            
            if (newLayoutedNodes.length > 0) {
              // Calculate the bounding box of new nodes
              const xMin = Math.min(...newLayoutedNodes.map(n => n.position.x))
              const xMax = Math.max(...newLayoutedNodes.map(n => n.position.x + NODE_WIDTH))
              const yMin = Math.min(...newLayoutedNodes.map(n => n.position.y))
              const yMax = Math.max(...newLayoutedNodes.map(n => n.position.y + NODE_HEIGHT))
              
              // Zoom to the bounding box
              reactFlowInstance.setViewport({
                x: -xMin + 50,
                y: -yMin + 50,
                zoom: 1
              }, { duration: 200 })
            }
          })
        }
      }
    } catch (error) {
      console.error('Error updating flow:', error)
    }
  }, [rootNode, convertedFlow, nodes, handleCreateDataNode, onSelectResponse, onRepath, reactFlowInstance])

  // Memoize position validation to prevent unnecessary updates
  const validateNodePositions = useCallback((nodes: Node[]) => {
    return nodes.map(node => ({
      ...node,
      position: {
        x: Number.isFinite(node.position.x) ? node.position.x : 0,
        y: Number.isFinite(node.position.y) ? node.position.y : 0
      }
    }))
  }, [])

  // Remove the layout effect since we handle it in the main effect
  useEffect(() => {
    if (nodes.length === 0) return

    const validatedNodes = validateNodePositions(nodes)
    if (JSON.stringify(nodes) !== JSON.stringify(validatedNodes)) {
      setNodes(validatedNodes)
    }
  }, [nodes, validateNodePositions])

  // Memoize node changes handler
  const onNodesChange = useCallback((changes) => {
    setNodes(nodes => 
      changes.reduce((acc, change) => {
        // Handle position changes
        if (change.type === 'position' && change.position) {
          return acc.map(node => {
            if (node.id === change.id) {
              // Ensure we have valid position values
              const newPosition = {
                x: typeof change.position.x === 'number' ? change.position.x : node.position.x,
                y: typeof change.position.y === 'number' ? change.position.y : node.position.y
              };
              return {
                ...node,
                position: newPosition
              };
            }
            return node;
          });
        }
        // Handle other changes (like selection)
        if (change.type === 'select') {
          return acc.map(node => 
            node.id === change.id ? { ...node, selected: change.selected } : node
          );
        }
        return acc;
      }, nodes)
    );
  }, [])

  // Handle edge connections
  const onConnect = useCallback((params: any) => {
    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);

    // Only allow connections:
    // 1. Between message nodes
    // 2. From data nodes to synthesizer nodes
    const isMessageConnection = !sourceNode?.type && !targetNode?.type;
    const isDataToSynthesizer = sourceNode?.type === 'data' && targetNode?.type === 'synthesizer';

    if (isMessageConnection || isDataToSynthesizer) {
      // For data to synthesizer connections, send a message to update the synthesizer
      if (isDataToSynthesizer) {
        onSendMessage({
          type: 'synthesizer',
          data: {
            dataNodeId: params.source,
            synthesizerNodeId: params.target
          }
        });
      }

      setEdges((eds) => addEdge({
        ...params,
        style: { stroke: '#333', strokeWidth: 2 }
      }, eds));
    }
  }, [nodes, onSendMessage]);

  // Memoize ReactFlow props to prevent unnecessary re-renders
  const reactFlowProps = useMemo(() => ({
    nodes,
    edges,
    onNodesChange,
    onConnect,
    nodeTypes,
    onInit: setReactFlowInstance,
    fitView: true,
    defaultViewport: { x: 0, y: 0, zoom: 1 },
    minZoom: 0.1,
    maxZoom: 2,
    className: "bg-background",
    style: { width: "100%", height: "100%" }
  }), [nodes, edges, onNodesChange, onConnect, nodeTypes])

  // Memoize handlers to prevent unnecessary re-renders
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault()
    event.stopPropagation()
    if (node.data.onClick) {
      node.data.onClick()
    }
    setCurrentNode(node)
  }, [])

  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault()
    event.stopPropagation()
    if (onRepath && node.data.message?.role === 'assistant') {
      onRepath(node.id)
    }
  }, [onRepath])

  const onPaneClick = useCallback(() => {}, [])

  // Handle command selection
  const handleCommandSelect = useCallback((command: string) => {
    if (readyState !== WebSocket.OPEN) {
      console.log('WebSocket not ready, state:', readyState)
      return
    }
    
    switch (command) {
      case 'photo': {
        const message: PhotoMessage = {
          type: 'photo',
          models: [],
          path: currentNode ? [currentNode.id] : undefined
        }
        onSendMessage(message as any)
        break
      }
      case 'describer': {
        const message: DescriberMessage = {
          type: 'describer',
          models: [],
          path: currentNode ? [currentNode.id] : undefined
        }
        onSendMessage(message as any)
        break
      }
      case 'synthesizer': {
        const message = {
          type: 'synthesizer',
          path: currentNode ? [currentNode.id] : undefined
        }
        onSendMessage(message)
        break
      }
    }
    setCommandOpen(false)
  }, [onSendMessage, currentNode, readyState])

  // Handle file drop
  const handleDrop = useCallback(async (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    // Get drop position in flow coordinates
    const position = project({
      x: event.clientX,
      y: event.clientY,
    })

    // Process each dropped file
    for (const file of files) {
      try {
        // For text files, read and send directly
        if (file.type.startsWith('text/')) {
          const content = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsText(file)
          })

          // Send text content directly
          const message = {
            type: 'data' as const,
            data: {
              filename: file.name,
              content: content,
              mimeType: file.type,
            },
            path: currentNode ? [currentNode.id] : undefined
          }
          onSendMessage(message)
        } else {
          // For binary files, upload to worker first
          const formData = new FormData()
          formData.append('file', file)

          const token = await getToken()
          const headers = withClerkAuthHeader(
            {
              'X-File-Name': encodeURIComponent(file.name),
              'Content-Type': file.type
            },
            token
          )
          const response = await fetch(`${API_URL}/ws/${sessionId}/upload`, {
            method: 'POST',
            headers,
            body: file
          });

          if (!response.ok) {
            throw new Error(`Upload failed: ${await response.text()}`);
          }

          const { id, url } = await response.json();

          // Send the file URL in the message
          const message = {
            type: 'data' as const,
            data: {
              filename: file.name,
              content: url,
              mimeType: file.type,
            },
            path: currentNode ? [currentNode.id] : undefined
          }
          onSendMessage(message)
        }
      } catch (error) {
        console.error('Error processing dropped file:', error)
      }
    }
  }, [project, currentNode, onSendMessage, sessionId])

  // Add drop event handlers
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  return (
    <div 
      className="relative w-full h-full"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <ReactFlow {...reactFlowProps}>
        <Background />
        <Controls />
      </ReactFlow>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50">
        <ChatInputNode
          onSendMessage={(message) => onSendMessage(message)}
          isLoading={isLoading}
          nodes={nodes}
        />
      </div>

      <CommandMenu 
        open={commandOpen}
        onOpenChange={setCommandOpen}
        onSelect={handleCommandSelect}
      />

      {children}
    </div>
  )
}

// Convert message tree to flow nodes and edges
function convertMessageTreeToFlow(message: Message): { nodes: Node[]; edges: Edge[] } {
  console.log('Starting message tree conversion:', {
    rootMessage: {
      id: message.id,
      role: message.role,
      type: message.metadata?.type,
      metadata: message.metadata,
      childCount: message.children?.length,
      nodeCount: Object.keys(message.nodes || {}).length
    }
  });

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const processedNodes = new Set<string>();

  // Process all nodes in the full state
  const processAllNodes = (msg: Message) => {
    // First process the current node
    processNode(msg, msg.parent_id);

    // Then process all children
    msg.children?.forEach(child => {
      if (child) {
        processAllNodes(child);
      }
    });
  };

  const processNode = (msg: Message, parentId: string | null = null) => {
    if (processedNodes.has(msg.id)) {
      console.log('Skipping already processed node:', msg.id);
      return;
    }
    processedNodes.add(msg.id);

    console.log('Processing node:', {
      id: msg.id,
      type: msg.metadata?.type,
      parentId,
      metadata: msg.metadata
    });

    const isIndependentNode = ['photo', 'data', 'synthesizer', 'describer'].includes(msg.metadata?.type || '');

    // Create node with guaranteed valid position
    const position = isIndependentNode 
      ? { 
          x: Math.max(0, Math.random() * 500 + 600),
          y: Math.max(0, Math.random() * 400 + 100)
        }
      : { x: 0, y: 0 };

    // Create node data
    const nodeData = isIndependentNode
      ? {
          id: msg.id,
          content: msg.content,
          metadata: msg.metadata,
          status: msg.metadata?.status || 'initial',
          error: msg.metadata?.error,
          imageB64: msg.metadata?.imageB64,
          input: msg.metadata?.prompt,
          filename: msg.metadata?.filename,
          synthesizedContent: msg.metadata?.synthesizedContent
        }
      : { message: msg };

    const node = {
      id: msg.id,
      type: msg.metadata?.type || 'message',
      position,
      draggable: true,
      connectable: true,
      data: nodeData
    };

    nodes.push(node);

    // Only create edges for non-independent nodes
    if (parentId && !isIndependentNode) {
      const isActive = msg.role === 'assistant' 
        ? msg.parent_id && message.metadata?.activeResponse === msg.id
        : true;

      edges.push({
        id: `${parentId}-${msg.id}`,
        source: parentId,
        target: msg.id,
        style: {
          stroke: isActive ? '#333' : '#ccc',
          strokeWidth: isActive ? 2 : 1,
          opacity: isActive ? 1 : 0.5
        }
      });
    }
  };

  // Process all nodes in the message tree
  processAllNodes(message);

  // Process any independent nodes at the top level
  if (message.nodes) {
    Object.values(message.nodes).forEach(node => {
      if (node && ['photo', 'data', 'synthesizer', 'describer'].includes(node.metadata?.type || '')) {
        processNode(node, null);
      }
    });
  }

  console.log('Message tree conversion complete:', {
    totalNodes: nodes.length,
    totalEdges: edges.length,
    nodeTypes: nodes.map(n => ({
      id: n.id,
      type: n.type
    }))
  });

  return { nodes, edges };
}

interface ChatInputNodeProps {
  onSendMessage: (message: string) => void
  isLoading: boolean
  nodes: Node[]
}

function ChatInputNode({ onSendMessage, isLoading, nodes }: ChatInputNodeProps) {
  const [message, setMessage] = useState('')
  const [isAnalyzingPhotos, setIsAnalyzingPhotos] = useState(false)

  const handleSend = useCallback(async () => {
    if (!message.trim()) return
    
    setIsAnalyzingPhotos(true)
    try {
      // Get all photos that are added to chat
      const photoNodes = nodes.filter(node => 
        node.type === 'photo' && 
        node.data.imageB64
      )

      // Analyze each photo
      // const photoDescriptions = await Promise.all(
      //   photoNodes.map(async node => {
      //     const result = await analyzeImage(node.data.imageB64)
      //     return result.description
      //   })
      // )

      // Combine message with photo descriptions
      let fullMessage = message.trim()
      // console.log('Photo descriptions:', photoDescriptions)
      // if (photoDescriptions.length > 0) {
      //   fullMessage += '\n\nIncluded Photos:\n' + 
      //     photoDescriptions.map((desc, i) => `[Photo ${i + 1}]: ${desc}`).join('\n')
      // }

      onSendMessage(fullMessage)
      setMessage('')
    } catch (err) {
      console.error('Error analyzing photos:', err)
    } finally {
      setIsAnalyzingPhotos(false)
    }
  }, [message, onSendMessage, nodes])

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend])

  return (
    <div className="flex flex-col gap-2 w-[600px] max-w-[90vw] bg-card p-4 rounded-lg shadow-lg border">
      <div className="text-sm text-muted-foreground">
        {nodes.length} node(s) in the conversation
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          disabled={isLoading || isAnalyzingPhotos}
          className="flex-1"
        />
        <Button 
          onClick={handleSend}
          disabled={isLoading || isAnalyzingPhotos || !message.trim()}
        >
          {isLoading || isAnalyzingPhotos ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  )
}
